import React, { useState } from 'react';
import {
  EventTimeAndLocationDetails,
  FallbackHeaderImage,
  OrgLogoTile,
  imageUrl,
  imageVersionFor,
} from '@charitystack/shared';
import FundraisingBar from '../FundraisingBar';
import { getGlobalImageUrl } from '../config/env';
import ClampedAbout from './ClampedAbout';
import OverlaySupporters from './OverlaySupporters';
import { OVERLAY_PANE_DESCRIPTION_CLASS } from './overlayContent';

/**
 * OverlayDetailPane — the overlay's left pane at 1024 and up (CON-520, Paper 7CM-0, 7NQ-0,
 * S8M-0). Replaces EmbedFormDetailPage in the overlay; the embed's details page keeps that
 * component.
 *
 * The hero sits flush at the top (the pane clips it to the card radius), then, 20 in from
 * the edges: title 24/30, the by-line with the 24 logo tile, the ProgressStat, the supporter
 * avatar row, About + the description, and for events the date / location tiles and the
 * Location map. The pane scrolls when its content is taller than the shell. About shows a few
 * lines, then Learn more opens the full description and the FAQs in a sheet over the pane
 * (ClampedAbout, the same link the embed's detail page uses).
 */
function OverlayDetailPane({
  data,
  brandColors,
  previewMode = false,
  headingLevel = 'h1',
  portalContainer,
  onGive,
}) {
  const HeadingTag = headingLevel;
  const formType = data?.formType || data?.elementType || '';
  const isEvent = formType === 'EVENT';
  const formID = data?.formID || data?.elementID || '';
  const merchantName = data?.nonprofitName || '';
  const organizedBy = data?.organizedBy || merchantName;
  const details = data?.details || data?.eventDetails || '';
  const brandColor = brandColors?.['accent-50'] || data?.lightColor;

  const [imageError, setImageError] = useState(false);
  const persistedImageUrl = imageUrl({
    base: getGlobalImageUrl(),
    type: isEvent ? 'event' : 'fundraiser',
    name: `${merchantName}_${formID}`,
    version: imageVersionFor(data || {}),
  });
  const imageSrc = previewMode ? data?.image : persistedImageUrl;
  const showFallback = !imageSrc || imageError;

  return (
    // data-pane-sheet-scope: the content FAQPaneDrawer covers, made inert while it is open.
    <div className="flex min-h-0 flex-1 flex-col" data-pane-sheet-scope="">
      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
        {/* Hero, flush top. */}
        <div className="relative aspect-video w-full shrink-0 bg-[color:var(--cs-surface-sunken)]">
          {showFallback ? (
            <FallbackHeaderImage
              alt="Default fundraiser image: heart-shaped forest"
              variant="donation-elements"
              brandColor={brandColor}
              title={data?.formName}
            />
          ) : (
            <img
              key={previewMode ? imageSrc : undefined}
              src={imageSrc}
              alt="Fundraiser Header"
              className="absolute inset-0 h-full w-full object-cover"
              onError={() => setImageError(true)}
              loading="eager"
            />
          )}
        </div>

        <div className="flex flex-col gap-[var(--cs-space-4)] px-5 pb-5 pt-5">
          <div className="flex flex-col gap-[var(--cs-space-1)]">
            <HeadingTag className="m-0 text-[length:var(--cs-text-2xl)] leading-[var(--cs-leading-30)] font-semibold tracking-[var(--cs-tracking-tight)] text-[color:var(--cs-text-primary)]">
              {data?.formName}
            </HeadingTag>
            {organizedBy && (
              <p className="m-0 flex items-center gap-[var(--cs-space-1)] text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)]">
                <span className="mr-[var(--cs-space-1)] inline-flex">
                  <OrgLogoTile
                    merchantName={merchantName}
                    logo={data?.logo}
                    logoVersion={data?.logoVersion}
                    s3LogoUrl={import.meta.env.VITE_S3_LOGO_URL}
                    size="sm"
                  />
                </span>
                <span className="text-[color:var(--cs-text-secondary)]">By</span>
                <span className="font-medium text-[color:var(--cs-brand-text,var(--cs-text-primary))]">
                  {organizedBy}
                </span>
              </p>
            )}
          </div>

          {/* Events show no progress and no supporters (Paper 6S3-0). */}
          {!isEvent && (
            <div className="flex flex-col gap-[var(--cs-space-3)] empty:hidden">
              {data?.showFundraisingBar && (
                <FundraisingBar
                  fundraisingBarID={
                    data.fundraisingBarElementID
                      ? data.fundraisingBarElementID
                      : previewMode
                        ? 'NEW_FORM'
                        : ''
                  }
                  brandColors={brandColors}
                  previewMode={previewMode}
                  amounts={{
                    targetAmount: data.targetAmount,
                    raisedAmount:
                      (parseInt(data.totalTransactionAmount) || 0) +
                      (parseInt(data.amountOffset) || 0),
                    trackingMetric: data.trackingMetric,
                    metricLabel: data.metricLabel,
                    elementOffset: data.elementOffset || 0,
                  }}
                />
              )}
              <OverlaySupporters
                formType={formType}
                enableDonorList={data?.enableDonorList}
                donorListID={data?.donorListElementID || null}
                donorListType={data?.donorListType}
                donorListData={data?.donorListData || null}
                showAllSupportersAnonymous={data?.showAllSupportersAnonymous}
                brandColors={brandColors}
                previewMode={previewMode}
                onGive={onGive}
                portalContainer={portalContainer}
              />
            </div>
          )}

          {isEvent && data?.enableTimeAndLocation !== false && (
            <EventTimeAndLocationDetails
              part="tiles"
              eventStartDate={data?.eventStartDate || data?.eventDate || null}
              eventEndDate={data?.eventEndDate || null}
              eventStartTime={data?.eventStartTime || null}
              eventEndTime={data?.eventEndTime || null}
              eventLocation={data?.eventLocation || null}
              timeZone={data?.timeZone || null}
            />
          )}

          {/* About: a few lines, then Learn more (full description + FAQs) or FAQs (ClampedAbout). */}
          <ClampedAbout
            html={details}
            descriptionClassName={OVERLAY_PANE_DESCRIPTION_CLASS}
            lineHeightVar="--cs-leading-22"
            title={data?.formName}
            faqs={data?.formFaqs || data?.customFAQs || null}
            elementID={data?.elementID || data?.formID}
          />

          {isEvent && data?.enableTimeAndLocation !== false && (
            <EventTimeAndLocationDetails part="map" eventLocation={data?.eventLocation || null} />
          )}
        </div>
      </div>
    </div>
  );
}

export default OverlayDetailPane;
